import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tabs, Tab } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/monitoring.actions'
import * as fromParticipantsActions from 'store/actions/participants.action'
import * as fromCourseActions from 'store/actions/courses.action'
import Loader from 'components/ui/loader/Loader'
import Chat from 'components/ui/chat/Chat'
import * as fromChatActions from 'store/actions/chat.action'
import { CourseStateType } from 'enum/course-state.enum'
import { useAccess } from 'contexts/AccessContext'
import Wrapper from 'components/reusables/wrapper'
import CourseResults from 'components/administration/components/monitor/components/user/components/courseResults/CourseResults'
import CourseData from 'components/administration/components/monitor/components/user/components/courseData/CourseData'
import { Evaluations } from 'components/administration/components/monitor/components/user/components/Evaluations'
import ParticipantCourseResultsHeader from './ParticipantCourseResultsHeader'
import styles from './ParticipantCourseResults.module.scss'

interface Params {
  userId: string
  courseId: string
}

/**
 * Resultados del curso desde ficha participante: mismo card Wrapper que la ficha.
 */
export const ParticipantCourseResults: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { courseId, userId } = useParams<Params>()
  const { course, loading: courseLoading } = useSelector(
    ({ courses }: IMainState) => courses
  )
  const { user, loading: userLoading } = useSelector(
    ({ monitoring }: IMainState) => monitoring
  )
  const { participant } = useSelector(
    ({ participants }: IMainState) => participants
  )
  const [loading, setLoading] = useState(false)
  const { user: tokenData } = useSelector(({ user }: IMainState) => user)
  const { can, canAccessCourse } = useAccess()
  const [conversationId, setConversationId] = useState(0)
  const [tabKey, setTabKey] = useState('results')

  const getUserMonitoring = useCallback(() => {
    dispatch({
      type: fromActions.USER_MONITORING_GET,
      payload: { courseId, userId },
    })
  }, [courseId, dispatch, userId])

  useEffect(() => {
    dispatch(fromActions.Actions.setLoading(false))
    dispatch(fromCourseActions.Actions.setCourseLoading(false))
    setLoading(true)
    getUserMonitoring()
    dispatch({
      type: fromCourseActions.GET_COURSE,
      payload: courseId,
    })
    dispatch({
      type: fromParticipantsActions.PARTICIPANTS_PROFILES_GET,
      payload: Number(userId),
    })
  }, [courseId, dispatch, userId, getUserMonitoring])

  useEffect(
    () => () => {
      dispatch(fromActions.Actions.setLoading(false))
      dispatch(fromCourseActions.Actions.setCourseLoading(false))
    },
    [dispatch]
  )

  useEffect(() => {
    if (user.conversationId >= 0) setConversationId(Number(user.conversationId))
  }, [user])

  useEffect(() => {
    setLoading(userLoading || courseLoading)
  }, [userLoading, courseLoading])

  const onSubmit = (data: any) => {
    dispatch({
      type: fromChatActions.CREATE_CHAT_MESSAGE,
      payload: { ...data, userId, identifier: tokenData.identifier },
    })
  }

  const results = (
    <>
      <CourseData courseData={course} hideTitle hideBanner />
      <CourseResults compact />
    </>
  )

  const resultsWithSpacing = <div className={styles.tabPanel}>{results}</div>

  const canShowChat = canAccessCourse(Number(courseId)) && can('manageMessages')
  const courseName = course?.name || '—'
  const showEvaluationsCard = (user.evaluations?.length ?? 0) > 0

  if (loading) return <Loader overlay color />

  return (
    <>
      <div className={styles.cardSection}>
        <Wrapper shadow>
          <ParticipantCourseResultsHeader
            participant={
              participant?.id === Number(userId) ? participant : undefined
            }
            monitoringUser={user}
            courseName={courseName}
          />
        </Wrapper>
      </div>

      <div className={styles.cardSection}>
        <Wrapper shadow>
          {canShowChat && course.state === CourseStateType.Published ? (
            <Tabs
              activeKey={tabKey}
              onSelect={(key) => setTabKey(key || '')}
              id="participant-course-results-tabs"
              className={styles.tabs}
            >
              <Tab
                eventKey="results"
                title={t('administration.monitor.tabs.results')}
              >
                {resultsWithSpacing}
              </Tab>
              {canShowChat && (
                <Tab
                  eventKey="messages"
                  title={t('administration.monitor.tabs.messages')}
                  className={styles.chatTab}
                >
                  <div className={styles.chatTabPanel}>
                    {tabKey === 'messages' && (
                      <Chat
                        fromModal
                        fromMonitoring={true}
                        onSend={onSubmit}
                        courseId={Number(courseId) || 0}
                        conversationId={conversationId}
                      />
                    )}
                  </div>
                </Tab>
              )}
            </Tabs>
          ) : (
            results
          )}
        </Wrapper>
      </div>

      {showEvaluationsCard && (
        <div className={styles.cardSection}>
          <Wrapper shadow>
            <h2 className={styles.sectionTitle}>
              {t('administration.monitor.tabs.evaluations')}
            </h2>
            <Evaluations
              evaluations={user.evaluations ?? []}
              onRefresh={getUserMonitoring}
            />
          </Wrapper>
        </div>
      )}
    </>
  )
}

export default ParticipantCourseResults
